import cn from 'classnames';
import React, { useMemo } from 'react';
import { useAppSelector } from '@app/hooks';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Divider } from '@react-md/divider';
import { TextField } from '@react-md/form';
import { Typography } from '@react-md/typography';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { SignUpRequest, useSignupCreateMutation } from '@services/df/auth';
import { FeatureContent } from '@components/feature-content';
import { FormField, NonFieldErrors, NON_FIELD_ERROR_KEY } from '@components/form-field';
import { SubmitButton } from '@components/submit-button';
import { FeatureKeys as FF, useFeatureFlags } from '@components/feature-flags';
import { selectAppSettings } from '@modules/app';
import { processEmailError } from '@modules/auth';
import { useUserFormLabels } from '@modules/user';
import { filterEmptyValues, setApiErrors } from '@utils';
import { useValidation } from '@hooks';
import { createSignupValidation } from '../form-validation';
import { AuthTitle } from '../components';
import { signUpId } from '../helpers';
import { SignUpFormId } from '../constants';

import styles from './signup-form.module.scss';

type SignupForm = SignUpRequest;

interface Props {
    onSuccess?: (data?: SignupForm) => void;
}

export const SignupBody: React.FC<Props> = ({ onSuccess, children }) => {
    const settings = useAppSelector(selectAppSettings);
    const [signup, { isLoading: isTokenObtaining }] = useSignupCreateMutation();
    const [isFirstNameRequired, isLastNameRequired, isPhoneRequired] = useFeatureFlags([
        FF.SignUp.FirstNameRequired,
        FF.SignUp.LastNameRequired,
        FF.SignUp.PhoneRequired,
    ]);
    const validationOptions = useMemo(
        () => ({
            isFirstNameRequired,
            isLastNameRequired,
            isPhoneRequired,
        }),
        [isFirstNameRequired, isLastNameRequired, isPhoneRequired],
    );

    const validationSchema = useValidation(createSignupValidation, validationOptions);
    const form = useForm<SignupForm>({
        defaultValues: {
            email: '',
            password: '',
            first_name: '',
            last_name: '',
            phone: '',
            // position: '',
            // company_title: '',
            company_name: settings.company_title,
        },
        resolver: yupResolver(validationSchema),
    });

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors },
        clearErrors,
    } = form;

    const submit: SubmitHandler<SignupForm> = async form => {
        try {
            await signup({
                signUpRequest: filterEmptyValues(form) as SignupForm,
            }).unwrap();

            onSuccess?.();
        } catch (error) {
            const { data } = error as FetchBaseQueryError;
            const errorObject = processEmailError(data);

            setApiErrors(setError, errorObject);
        }
    };

    const { t } = useTranslation();
    const labels = useUserFormLabels(t);
    return (
        <form id={SignUpFormId} onSubmit={handleSubmit(submit)}>
            <div className={cn('fieldset-grid', 'base-paddings')}>
                <AuthTitle
                    title={t('titles.signUp', 'Sign up to {{company}}', {
                        company: settings.company_full_title || settings.company_title,
                    })}
                />

                <FormField
                    prefix={SignUpFormId}
                    name="email"
                    control={control}
                    component={TextField}
                    label={labels.email}
                    widgetProps={{
                        onChange: () => {
                            clearErrors('email');
                        },
                    }}
                />

                <NonFieldErrors errors={errors} />

                <FormField
                    prefix={SignUpFormId}
                    name="password"
                    control={control}
                    component={TextField}
                    label={labels.password}
                    hint={
                        <Typography type="subtitle-2" component="span">
                            {t('errors.minLength', 'At least {{min}} characters long', { min: 10 })}
                        </Typography>
                    }
                    widgetProps={{
                        type: 'password',
                    }}
                />
            </div>

            <Divider />

            <div className={cn('base-paddings', 'no-top', 'fieldset-grid', 'fieldset-grid--columns', styles.columns)}>
                <FormField
                    prefix={SignUpFormId}
                    name="first_name"
                    control={control}
                    component={TextField}
                    label={labels.first_name}
                />
                <FormField
                    prefix={SignUpFormId}
                    name="last_name"
                    control={control}
                    component={TextField}
                    label={labels.last_name}
                />
                <FormField
                    prefix={SignUpFormId}
                    name="phone"
                    control={control}
                    component={TextField}
                    label={labels.phone}
                    className="fieldset-grid-cell--full"
                />
                {/*<FormField prefix={SignUpFormId}*/}
                {/*           name="position"*/}
                {/*           control={control}*/}
                {/*           component={TextField}*/}
                {/*           labels={labels}*/}
                {/*/>*/}
                {/*<FormField prefix={SignUpFormId}*/}
                {/*           name="company_title"*/}
                {/*           control={control}*/}
                {/*           component={TextField}*/}
                {/*           labels={labels}*/}
                {/*/>*/}

                <div className={cn('flex', 'fieldset-grid-cell--full')}>
                    <SubmitButton prefix={signUpId()} isSending={isTokenObtaining}>
                        <FeatureContent contentKey={signUpId('submit')} fallback={t('buttons.signUp', 'Sign up')} />
                    </SubmitButton>
                </div>
            </div>
        </form>
    );
};
